import { useEffect, useRef, useState, useCallback } from "react";
import { Check, Download, FolderDown, Pencil, Save, Search, Trash2, Upload, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { RecycleBin } from "@/components/RecycleBin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";

const labelCls = "text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono";
const iconBtn = "p-1.5 rounded text-slate-400 hover:text-amber-300 hover:bg-[#1A2232] transition-colors";

const CONFIG_KEYS = ["orientation", "shape", "unit", "diameter", "width", "height", "tankLength", "fill", "liquidName", "customDensity", "mixA", "mixB", "mixPct", "calibData", "calibReadMode", "calibLevel", "calibVolume", "compEnabled", "compCount", "comps"];

export const SavedConfigs = ({ getSnapshot, onLoad, isAdmin, isMobileView }) => {
  const [configs, setConfigs] = useState([]);
  const [name, setName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [binOpen, setBinOpen] = useState(false);
  const [binCount, setBinCount] = useState(0);
  const fileRef = useRef(null);
  const listRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/configs");
      setConfigs(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load configurations");
    }
    if (isAdmin) {
      try {
        const { data } = await api.get("/configs/bin");
        setBinCount(data.length);
      } catch {
        /* bin count is cosmetic */
      }
    }
  }, [isAdmin]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const existing = configs.find((c) => c.name.trim().toLowerCase() === name.trim().toLowerCase());

  const q = searchQuery.trim().toLowerCase();
  const filtered = q
    ? configs.filter((c) => {
        const badges = `${c.config?.orientation ?? ""} ${c.config?.shape ?? ""} ${c.config?.unit ?? ""}`.toLowerCase();
        return c.name.toLowerCase().includes(q) || badges.includes(q);
      })
    : configs;

  const handleSave = async () => {
    const n = name.trim();
    if (!n) {
      toast.error("Enter a configuration name first");
      return;
    }
    try {
      await api.post("/configs", { name: n, config: getSnapshot() });
      await refresh();
      setName("");
      toast.success(existing ? `Overwritten: ${n}` : `Saved: ${n}`);
      listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/configs/${id}`);
      await refresh();
      toast.success("Configuration moved to recycle bin");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const confirmRename = async (cfg) => {
    const n = renameValue.trim();
    if (!n) return;
    try {
      await api.put(`/configs/${cfg.id}`, { name: n, config: cfg.config });
      await refresh();
      setRenamingId(null);
      toast.success("Configuration renamed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rename failed");
    }
  };

  const handleExport = () => {
    const blob = new Blob([JSON.stringify(configs, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tank-configs.json";
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${configs.length} configuration${configs.length === 1 ? "" : "s"}`);
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const data = JSON.parse(await file.text());
      if (!Array.isArray(data)) throw new Error("bad format");
      const valid = data.filter((c) => c && typeof c.name === "string" && c.name.trim() && c.config && typeof c.config === "object");
      if (valid.length === 0) throw new Error("no valid configs");
      let imported = 0;
      for (const c of valid) {
        const config = Object.fromEntries(
          CONFIG_KEYS.filter((k) => c.config[k] !== undefined).map((k) => [
            k,
            typeof c.config[k] === "object" ? c.config[k] : String(c.config[k]),
          ])
        );
        await api.post("/configs", { name: c.name.trim(), config });
        imported += 1;
      }
      await refresh();
      toast.success(`Imported ${imported} configuration${imported === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response ? formatApiErrorDetail(e.response?.data?.detail) : "Import failed — not a valid config file");
    }
  };

  return (
    <div data-testid="saved-configs-section">
      <div className="flex items-center justify-between">
        <p className={labelCls}>Saved Configurations {!isAdmin && <span className="text-slate-600 normal-case">(shared · read-only)</span>}</p>
        {isAdmin && (
          <div className="flex items-center gap-1">
            <button title="Recycle bin" data-testid="recycle-bin-btn" onClick={() => setBinOpen(true)} className={`${iconBtn} relative`}>
              <Trash2 className="h-3.5 w-3.5" />
              {binCount > 0 && (
                <span data-testid="recycle-bin-count" className="absolute -top-1 -right-1 h-4 min-w-4 px-0.5 rounded-full bg-red-500/90 text-white text-[9px] font-mono flex items-center justify-center">
                  {binCount}
                </span>
              )}
            </button>
            <button title="Export configurations as JSON" data-testid="export-configs-btn" onClick={handleExport} disabled={configs.length === 0} className={`${iconBtn} disabled:opacity-40 disabled:pointer-events-none`}>
              <Download className="h-3.5 w-3.5" />
            </button>
            <button title="Import configurations from JSON" data-testid="import-configs-btn" onClick={() => fileRef.current?.click()} className={iconBtn}>
              <Upload className="h-3.5 w-3.5" />
            </button>
            <input ref={fileRef} type="file" accept=".json,application/json" data-testid="import-configs-input" className="hidden" onChange={handleImportFile} />
          </div>
        )}
      </div>
      {isAdmin && (
        <div className="flex gap-2 mt-2">
          <Input
            data-testid="save-config-name-input"
            placeholder="Configuration name…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
            }}
            className="bg-[#0D131E] border-[#243046] font-mono text-slate-100 text-sm focus-visible:ring-amber-500"
          />
          <Button data-testid="save-config-btn" onClick={handleSave} className={`shrink-0 font-semibold ${existing ? "bg-cyan-600 hover:bg-cyan-500 text-white" : "bg-amber-500 text-[#0B0F17] hover:bg-amber-400"}`}>
            <Save />
            {existing ? "Overwrite" : "Save"}
          </Button>
        </div>
      )}
      {configs.length > 0 && (
        <div className="relative mt-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
          <Input
            data-testid="config-search-input"
            placeholder="Search by name or type…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setSearchQuery("");
            }}
            className="pl-9 pr-8 bg-[#0D131E] border-[#243046] font-mono text-slate-100 text-xs focus-visible:ring-amber-500"
          />
          {searchQuery && (
            <button data-testid="config-search-clear" onClick={() => setSearchQuery("")} title="Clear search" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
      {configs.length === 0 ? (
        <p className="text-[11px] text-slate-500 font-mono mt-2" data-testid="no-saved-configs">{isAdmin ? "No saved configurations yet — name one and hit Save." : "No shared configurations available yet."}</p>
      ) : filtered.length === 0 ? (
        <p className="text-[11px] text-slate-500 font-mono mt-2" data-testid="no-search-matches">No matches for &ldquo;{searchQuery.trim()}&rdquo;.</p>
      ) : (
        <>
          {q && (
            <p className="text-[10px] font-mono text-slate-500 mt-2" data-testid="search-match-count">
              {filtered.length} of {configs.length} shown
            </p>
          )}
          <ul ref={listRef} className={`mt-2 space-y-1.5 overflow-y-auto pr-1 ${isMobileView ? "max-h-[230px]" : "max-h-[365px]"}`} data-testid="saved-configs-list">
            {filtered.map((c, i) => (
            <li key={c.id} data-testid={`saved-config-row-${i}`} className="flex items-center gap-1.5 px-2.5 py-2 rounded-lg border border-[#243046] bg-[#0D131E]">
              {renamingId === c.id ? (
                <>
                  <Input
                    data-testid={`rename-input-${i}`}
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") confirmRename(c);
                      if (e.key === "Escape") setRenamingId(null);
                    }}
                    className="h-7 flex-1 bg-[#131924] border-[#243046] font-mono text-xs text-slate-100"
                    autoFocus
                  />
                  <button title="Confirm rename" data-testid={`confirm-rename-btn-${i}`} onClick={() => confirmRename(c)} className={iconBtn}>
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button title="Cancel" data-testid={`cancel-rename-btn-${i}`} onClick={() => setRenamingId(null)} className={iconBtn}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs font-mono text-slate-200 truncate" data-testid={`saved-config-name-${i}`}>{c.name}</span>
                    <span className="block text-[10px] font-mono text-slate-500">{c.config.orientation} · {c.config.shape} · {c.config.unit}</span>
                  </span>
                  <button title="Load" data-testid={`load-config-btn-${i}`} onClick={() => { onLoad(c.config, c.name); toast.success(`Loaded: ${c.name}`); }} className={iconBtn}>
                    <FolderDown className="h-3.5 w-3.5" />
                  </button>
                  {isAdmin && (
                    <>
                      <button title="Rename" data-testid={`rename-config-btn-${i}`} onClick={() => { setRenamingId(c.id); setRenameValue(c.name); }} className={iconBtn}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button title="Delete" data-testid={`delete-config-btn-${i}`} onClick={() => handleDelete(c.id)} className={iconBtn}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </>
              )}
            </li>
            ))}
          </ul>
        </>
      )}
      <RecycleBin open={binOpen} onOpenChange={setBinOpen} onChanged={refresh} />
    </div>
  );
};
